/* ==========================================================================
   IAMONEAI - Theme Configuration
   Manages dark/light mode and theme-related utilities
   ========================================================================== */

import { STORAGE_KEYS } from './constants';

// ==========================================================================
// THEME VALUES
// ==========================================================================

export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

export const DEFAULT_THEME = THEMES.DARK;


// ==========================================================================
// THEME DETECTION
// ==========================================================================

/**
 * Get the system's preferred color scheme
 * @returns {'light' | 'dark'}
 */
export const getSystemTheme = () => {
  if (typeof window === 'undefined') return THEMES.DARK;
  
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? THEMES.DARK
    : THEMES.LIGHT;
};

/**
 * Check if system prefers reduced motion
 * @returns {boolean}
 */
export const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Check if system prefers high contrast
 * @returns {boolean}
 */
export const prefersHighContrast = () => {
  if (typeof window === 'undefined') return false;
  
  return window.matchMedia('(prefers-contrast: high)').matches;
};


// ==========================================================================
// THEME STORAGE
// ==========================================================================

/**
 * Get stored theme preference from localStorage
 * @returns {'light' | 'dark' | 'system' | null}
 */
export const getStoredTheme = () => {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.THEME);
    if (stored && Object.values(THEMES).includes(stored)) {
      return stored;
    }
    return null;
  } catch (error) {
    console.warn('Failed to read theme from localStorage:', error);
    return null;
  }
};

/**
 * Store theme preference in localStorage
 * @param {'light' | 'dark' | 'system'} theme
 */
export const setStoredTheme = (theme) => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(STORAGE_KEYS.THEME, theme);
  } catch (error) {
    console.warn('Failed to save theme to localStorage:', error);
  }
};

/**
 * Remove stored theme preference
 */
export const clearStoredTheme = () => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem(STORAGE_KEYS.THEME);
  } catch (error) {
    console.warn('Failed to clear theme from localStorage:', error);
  }
};


// ==========================================================================
// THEME APPLICATION
// ==========================================================================

/**
 * Resolve the actual theme to apply (handles 'system' preference)
 * @param {'light' | 'dark' | 'system'} theme
 * @returns {'light' | 'dark'}
 */
export const resolveTheme = (theme) => {
  if (theme === THEMES.SYSTEM) {
    return getSystemTheme();
  }
  return theme;
};

/**
 * Apply theme to the document
 * @param {'light' | 'dark'} theme - The resolved theme (not 'system')
 */
export const applyTheme = (theme) => {
  if (typeof document === 'undefined') return;
  
  const resolvedTheme = resolveTheme(theme);
  
  // Set data-theme attribute on html element
  document.documentElement.setAttribute('data-theme', resolvedTheme);
  
  // Update meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute(
      'content',
      resolvedTheme === THEMES.DARK ? '#0a0a0f' : '#f8fafc'
    );
  }
  
  // Update color-scheme for native elements
  document.documentElement.style.colorScheme = resolvedTheme;
};

/**
 * Initialize theme on app load
 * Priority: stored preference > system preference > default
 * @returns {'light' | 'dark' | 'system'}
 */
export const initializeTheme = () => {
  const stored = getStoredTheme();
  const theme = stored || DEFAULT_THEME;
  
  applyTheme(theme);
  
  return theme;
};


// ==========================================================================
// SYSTEM THEME LISTENER
// ==========================================================================

/**
 * Listen for system theme changes
 * @param {Function} callback - Called with new theme when system preference changes
 * @returns {Function} Cleanup function to remove listener
 */
export const onSystemThemeChange = (callback) => {
  if (typeof window === 'undefined') return () => {};
  
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  
  const handler = (event) => {
    callback(event.matches ? THEMES.DARK : THEMES.LIGHT);
  };
  
  // Modern browsers
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }
  
  // Legacy browsers
  mediaQuery.addListener(handler);
  return () => mediaQuery.removeListener(handler);
};


// ==========================================================================
// THEME COLORS (for JS access when needed)
// ==========================================================================

export const THEME_COLORS = {
  dark: {
    bgApp: '#0a0a0f',
    bgPanel: '#12121a',
    bgCard: '#1a1a24',
    bgHover: '#252530',
    textPrimary: '#ffffff',
    textSecondary: '#a1a1aa',
    textTertiary: '#71717a',
    borderDefault: '#27272a',
    borderLight: '#3f3f46',
    colorPrimary: '#14b8a6',
    colorSecondary: '#6366f1',
  },
  light: {
    bgApp: '#f8fafc',
    bgPanel: '#ffffff',
    bgCard: '#f1f5f9',
    bgHover: '#e2e8f0',
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    textTertiary: '#64748b',
    borderDefault: '#e2e8f0',
    borderLight: '#cbd5e1',
    colorPrimary: '#0d9488',
    colorSecondary: '#4f46e5',
  },
};

/**
 * Get a specific color value for the current theme
 * @param {'light' | 'dark'} theme
 * @param {string} colorKey
 * @returns {string}
 */
export const getThemeColor = (theme, colorKey) => {
  const resolvedTheme = resolveTheme(theme);
  return THEME_COLORS[resolvedTheme]?.[colorKey] || '';
};


// ==========================================================================
// THEME TRANSITION
// ==========================================================================

/**
 * Smoothly transition between themes
 * Adds a temporary class to disable transitions, changes theme, then re-enables
 * @param {'light' | 'dark' | 'system'} newTheme
 */
export const transitionTheme = (newTheme) => {
  if (typeof document === 'undefined') return;
  
  // Check if user prefers reduced motion
  if (prefersReducedMotion()) {
    applyTheme(newTheme);
    setStoredTheme(newTheme);
    return;
  }
  
  // Add transition class
  document.documentElement.classList.add('theme-transitioning');
  
  // Apply the new theme
  applyTheme(newTheme);
  setStoredTheme(newTheme);
  
  // Remove transition class after animation completes
  setTimeout(() => {
    document.documentElement.classList.remove('theme-transitioning');
  }, 300);
};


// ==========================================================================
// CSS FOR THEME TRANSITIONS (add to your CSS)
// ==========================================================================

/*
  Add this to your CSS for smooth theme transitions:
  
  html.theme-transitioning,
  html.theme-transitioning *,
  html.theme-transitioning *::before,
  html.theme-transitioning *::after {
    transition: background-color 0.3s ease,
                color 0.3s ease,
                border-color 0.3s ease,
                box-shadow 0.3s ease !important;
  }
*/


// ==========================================================================
// CONTEXT COLORS (Quick access for memory contexts)
// ==========================================================================

export const CONTEXT_COLORS = {
  personal: {
    color: '#14b8a6',
    bg: 'rgba(20, 184, 166, 0.1)',
    border: 'rgba(20, 184, 166, 0.3)',
  },
  work: {
    color: '#3b82f6',
    bg: 'rgba(59, 130, 246, 0.1)',
    border: 'rgba(59, 130, 246, 0.3)',
  },
  family: {
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.1)',
    border: 'rgba(245, 158, 11, 0.3)',
  },
};


// ==========================================================================
// STATUS COLORS
// ==========================================================================

export const STATUS_COLORS = {
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
};


// ==========================================================================
// EXPORT DEFAULT
// ==========================================================================

export default {
  THEMES,
  DEFAULT_THEME,
  getSystemTheme,
  prefersReducedMotion,
  prefersHighContrast,
  getStoredTheme,
  setStoredTheme,
  clearStoredTheme,
  resolveTheme,
  applyTheme,
  initializeTheme,
  onSystemThemeChange,
  THEME_COLORS,
  getThemeColor,
  transitionTheme,
  CONTEXT_COLORS,
  STATUS_COLORS,
};